import clsx from "clsx";
import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  FC,
  ReactNode,
} from "react";

type Size = "sm" | "lg";

type CommonProps = {
  // Optional so `<Link />` works as a `Trans` component tag (i18next injects
  // the translated segment as children at runtime).
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  inverted?: boolean;
  size?: Size;
};

type AnchorProps = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className"> & {
    href: string;
  };

type ButtonProps = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "type"> & {
    href?: undefined;
    type?: "button" | "submit";
  };

type LinkProps = AnchorProps | ButtonProps;

const sizeClassName: Record<Size, string> = {
  lg: "!py-2 !text-base",
  sm: "!py-1.5 !text-sm",
};

export const Link: FC<LinkProps> = ({
  children,
  className,
  disabled = false,
  inverted = false,
  size = "lg",
  ...rest
}) => {
  const sharedClassName = clsx(
    // Bang-prefixed: a global legacy stylesheet resets `a:focus`
    // (outline/box-shadow) with plain (non-!important) declarations that
    // would otherwise win the cascade over same-specificity Tailwind
    // utilities.
    "!inline-flex !items-center !justify-center !gap-1 !border-b !border-solid !font-semibold !tracking-[0.32px] !no-underline",
    // Thicker underline on hover/focus: compensate the +1px border with -1px
    // margin so siblings (and the page footer) do not shift.
    "hover:!-mb-px hover:!border-b-2 focus-visible:!-mb-px focus-visible:!border-b-2",
    "focus-visible:!rounded-lg focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-4",
    inverted
      ? "!border-foreground-primary-inverse !text-foreground-primary-inverse focus-visible:!outline-foreground-primary-inverse"
      : "!border-foreground-primary !text-foreground-primary focus-visible:!outline-foreground-primary",
    disabled &&
      (inverted
        ? "!pointer-events-none !cursor-not-allowed !border-foreground-tertiary-inverse !text-foreground-tertiary-inverse"
        : "!pointer-events-none !cursor-not-allowed !border-foreground-tertiary !text-foreground-tertiary"),
    sizeClassName[size],
    className,
  );

  if (rest.href) {
    const { href, tabIndex, onClick, ...anchorRest } = rest as AnchorProps;

    return (
      <a
        href={disabled ? undefined : href}
        tabIndex={disabled ? -1 : tabIndex}
        onClick={disabled ? (event) => event.preventDefault() : onClick}
        className={sharedClassName}
        aria-disabled={disabled}
        {...anchorRest}
      >
        {children}
      </a>
    );
  }

  const { type = "button", ...buttonRest } = rest as ButtonProps;

  return (
    <button
      type={type}
      className={sharedClassName}
      disabled={disabled}
      {...buttonRest}
    >
      {children}
    </button>
  );
};
